import {FC, useState, useEffect, useContext} from 'react'
import {IHandleFilterCallbackFunction, ILanguage} from '../../types/common'
import MultiSelectDropdown from '../../common/MultiSelectDropdown'
import YearMonthPicker from '../../common/YearMonthPicker'
import ActiveFilters from './ActiveFilters'
import {IVisibleColumnsOption} from '../../common/MultiSelectDropdown'
import axios from 'axios'
import Tooltip from '../../common/Tooltip'
import {useTranslation} from 'react-i18next'
import InfoBox from '../../common/InfoBox'
import {SessionContext, SessionContextType} from '../../common/ctx/SessionContext'

const FiltersFIO: FC<{
    language: ILanguage
    handleFilter: IHandleFilterCallbackFunction
    ctx: any
}> = ({language, handleFilter, ctx}) => {
    const {t} = useTranslation()
    const sessionCtx: SessionContextType = useContext(SessionContext)
    const [excludedDays, setExcludedDays] = useState<Array<Date>>()
    const [selectedDate, setSelectedDate] = useState<Date | undefined>()
    const [datePickerMinDate, setDatePickerMinDate] = useState<Date | undefined>()
    const [datePickerMaxDate, setDatePickerMaxDate] = useState<Date | undefined>()
    const [selectedUnitCategories, setSelectedUnitCategories] = useState<Array<string>>([])
    const [selectedSegments, setSelectedSegments] = useState<Array<string>>([])
    const [selectedProducts, setSelectedProducts] = useState<Array<string>>([])
    const [selectedUmbrellas, setSelectedUmbrellas] = useState<Array<string>>([])
    const [selectedSris, setSelectedSris] = useState<Array<number>>([])
    const [selectedInvestmentHorizons, setSelectedInvestmentHorizons] = useState<Array<string>>([])
    const [selectedIsEsg, setSelectedIsEsg] = useState<boolean>(false)
    const [searchByName, setSearchByName] = useState<string>('')
    const [toggleFilters, setToggleFilters] = useState<boolean>(false)
    const [unitCategoryOptions, setUnitCategoryOptions] = useState<Array<IVisibleColumnsOption>>([])
    const [segmentOptions, setSegmentOptions] = useState<Array<IVisibleColumnsOption>>([])
    const [sriOptions, setSriOptions] = useState<Array<IVisibleColumnsOption>>([])
    const [productOptions, setProductOptions] = useState<Array<IVisibleColumnsOption>>([])
    const [umbrellaOptions, setUmbrellaOptions] = useState<Array<IVisibleColumnsOption>>([])
    const [investmentHorizonOptions, setInvestmentHorizonOptions] = useState<Array<IVisibleColumnsOption>>([])
    const [productTooltipTxt, setProductTooltipTxt] = useState<string>('')
    const [umbrellaTooltipTxt, setUmbrellaTooltipTxt] = useState<string>('')
    const [segmentTooltipTxt, setSegmentTooltipTxt] = useState<string>('')
    const [defaultFilters, setDefaultFilters] = useState<object>({})

    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_API_BASE_URL}/api/filters/fio`, {
                headers: {
                    Accept: 'application/json',
                },
                auth: {
                    username: `${process.env.REACT_APP_API_USERNAME}`,
                    password: `${process.env.REACT_APP_API_PASSWORD}`,
                },
            })
            .then((result) => {
                const filters = result.data.filters
                const transformedUmbrellaOptions = filters.umbrella.map((item: IVisibleColumnsOption) => {
                    return {...item, icon: item.value}
                })

                setUnitCategoryOptions(filters.unitCategory)
                setSegmentOptions(filters.segment)
                setSriOptions(filters.sri)
                setProductOptions(filters.product)
                setUmbrellaOptions(transformedUmbrellaOptions)
                setInvestmentHorizonOptions(filters.investmentHorizon)
                setDefaultFilters(filters)
            })
            .catch((error) => {
                console.error(error)
            })
    }, [])

    useEffect(() => {
        ctx.isDefault()
    }, [])

    useEffect(() => {
        setSelectedUnitCategories(ctx.unitCategories)
        handleFilter(ctx.unitCategories, 'unitCategory')
    }, [ctx.unitCategories])

    useEffect(() => {
        setSelectedSegments(ctx.segments)
        handleFilter(ctx.segments, 'segment')
    }, [ctx.segments])

    useEffect(() => {
        setSelectedProducts(ctx.products)
        handleFilter(ctx.products, 'products') //fund item.products search
    }, [ctx.products])

    useEffect(() => {
        setSelectedUmbrellas(ctx.umbrellas)
        handleFilter(ctx.umbrellas, 'umbrellas') //fund item.umbrellas search
    }, [ctx.umbrellas])

    useEffect(() => {
        setSelectedSris(ctx.sris)
        handleFilter(ctx.sris, 'sri')
    }, [ctx.sris])

    useEffect(() => {
        setSelectedInvestmentHorizons(ctx.investmentHorizons)
        handleFilter(ctx.investmentHorizons, 'investmentHorizon')
    }, [ctx.investmentHorizons])

    useEffect(() => {
        setSelectedIsEsg(ctx.isEsg)
        handleFilter(ctx.isEsg, 'isEsg')
    }, [ctx.isEsg])

    useEffect(() => {
        setDatePickerMaxDate(ctx.maxDate)
        setDatePickerMinDate(ctx.minDate)
    }, [ctx.minDate, ctx.maxDate])

    useEffect(() => {
        setSelectedDate(ctx.date)
    }, [ctx.date])

    useEffect(() => {
        setExcludedDays(ctx.holiday)
    }, [ctx.holiday])

    useEffect(() => {
        setToggleFilters(ctx.showMoreFilters)
    }, [ctx.showMoreFilters])

    const handleSearch = () => {
        handleFilter(searchByName, 'name')
    }

    useEffect(() => {
        handleSearch()
    }, [searchByName])

    useEffect(() => {
        if (productOptions && ctx.products.length) {
            setProductTooltipTxt(() => {
                const filtered = productOptions.filter((item: IVisibleColumnsOption) => {
                    if (ctx.products.includes(item.value)) {
                        return item
                    }
                })

                return filtered.map((item) => item.label).join(', ')
            })
        }
    }, [productOptions, ctx.products])

    useEffect(() => {
        if (umbrellaOptions && ctx.umbrellas.length) {
            setUmbrellaTooltipTxt(() => {
                const filtered = umbrellaOptions.filter((item: IVisibleColumnsOption) => {
                    if (ctx.umbrellas.includes(item.value)) {
                        return item
                    }
                })

                return filtered.map((item) => item.label).join(', ')
            })
        }
    }, [umbrellaOptions, ctx.umbrellas])

    useEffect(() => {
        if (segmentOptions && ctx.segments.length) {
            setSegmentTooltipTxt(() => {
                const filtered = segmentOptions.filter((item: IVisibleColumnsOption) => {
                    if (ctx.segments.includes(item.value)) {
                        return item
                    }
                })

                return filtered.map((item) => t(item.label)).join(', ')
            })
        }
    }, [segmentOptions, ctx.segments])

    const handleUnitCategoryChange = (value: string): void => {
        setSelectedUnitCategories((prevState: Array<string>) => {
            const items = [...prevState]
            if (items.includes(value)) {
                return items.filter((item) => item != value)
            } else {
                items.push(value)
                return items
            }
        })

        ctx.setUnitCategories((prevState: Array<string>) => {
            const items = [...prevState]
            if (items.includes(value)) {
                return items.filter((item) => item != value)
            } else {
                items.push(value)
                return items
            }
        })
    }

    const handleSegmentChange = (value: string): void => {
        setSelectedSegments((prevState: Array<string>) => {
            const items = [...prevState]
            if (items.includes(value)) {
                return items.filter((item) => item != value)
            } else {
                items.push(value)
                return items
            }
        })

        ctx.setSegments((prevState: Array<string>) => {
            const items = [...prevState]
            if (items.includes(value)) {
                return items.filter((item) => item != value)
            } else {
                items.push(value)
                return items
            }
        })
    }

    const handleProductChange = (value: string): void => {
        setSelectedProducts((prevState: Array<string>) => {
            const items = [...prevState]
            if (items.includes(value)) {
                return items.filter((item) => item != value)
            } else {
                items.push(value)
                return items
            }
        })

        ctx.setProducts((prevState: Array<string>) => {
            const items = [...prevState]
            if (items.includes(value)) {
                return items.filter((item) => item != value)
            } else {
                items.push(value)
                return items
            }
        })
    }

    const handleUmbrellaChange = (value: string): void => {
        setSelectedUmbrellas((prevState: Array<string>) => {
            const items = [...prevState]
            if (items.includes(value)) {
                return items.filter((item) => item != value)
            } else {
                items.push(value)
                return items
            }
        })

        ctx.setUmbrellas((prevState: Array<string>) => {
            const items = [...prevState]
            if (items.includes(value)) {
                return items.filter((item) => item != value)
            } else {
                items.push(value)
                return items
            }
        })
    }

    const handleSriChange = (value: string): void => {
        setSelectedSris((prevState: Array<number>) => {
            const items = [...prevState]

            if (items.includes(parseInt(value))) {
                return items.filter((item) => item !== parseInt(value))
            } else {
                items.push(parseInt(value))
                return items
            }
        })

        ctx.setSris((prevState: Array<string>) => {
            const items = [...prevState]
            if (items.includes(value)) {
                return items.filter((item) => item != value)
            } else {
                items.push(value)
                return items
            }
        })
    }

    const handleInvestmentHorizonChange = (value: string): void => {
        setSelectedInvestmentHorizons((prevState: Array<string>) => {
            const items = [...prevState]
            if (items.includes(value)) {
                return items.filter((item) => item != value)
            } else {
                items.push(value)
                return items
            }
        })

        ctx.setInvestmentHorizons((prevState: Array<string>) => {
            const items = [...prevState]
            if (items.includes(value)) {
                return items.filter((item) => item != value)
            } else {
                items.push(value)
                return items
            }
        })
    }

    const handleIsEsgChange = (isEsgCheckbox: boolean): void => {
        setSelectedIsEsg(() => {
            return isEsgCheckbox
        })
        ctx.setIsEsg(() => {
            return isEsgCheckbox
        })
    }

    const handleToggleFilters = (): void => {
        setToggleFilters(!toggleFilters)
        ctx.setShowMoreFilters(!toggleFilters)
    }

    return (
        <>
            <section id="container-toolbar" className="flex">
                {sessionCtx?.getItem(ctx.STORAGE_KEY) !== 'hidden' && (
                    <InfoBox
                        title={
                            <span>
                                {' '}
                                {t('label.infobox.filters.save')} <br />
                                {t('label.infobox.filters.continue')}{' '}
                            </span>
                        }
                        onCloseCallback={() => {
                            sessionCtx.setItem(ctx.STORAGE_KEY, 'hidden')
                        }}
                    />
                )}
                <div className="filter-bar flex">
                    <div className="up-filter-bar">
                        <div className="grid-column grid-column-fio flex">
                            <div className="grid-column-first flex">
                                <div className="datepickers-item flex">
                                    <label className="datepickers-item-label">{t('label.datepicker.title2')}</label>
                                    <YearMonthPicker
                                        language={language}
                                        selectedDate={selectedDate}
                                        setSelectedDate={setSelectedDate}
                                        datePickerMinDate={datePickerMinDate}
                                        datePickerMaxDate={datePickerMaxDate}
                                        excludeDates={excludedDays}
                                        withCurrentDate={true}
                                        filterDate={(date) => {
                                            return date.getDay() !== 0 && date.getDay() !== 6
                                        }}
                                        onDateChangeCallback={(date) => {
                                            ctx.setDate(date)
                                        }}
                                    />
                                </div>
                                <div className="filter-item input-rwd">
                                    <label className="filter-item-label">{t('label.unitCategory.title')}</label>
                                    <MultiSelectDropdown
                                        options={unitCategoryOptions}
                                        selected={selectedUnitCategories}
                                        toggleOption={handleUnitCategoryChange}
                                        forLabel="filter_unitCategory"
                                        tooltipTxt={t('label.unitCategory.tooltip')}
                                        visibletooltip={false}
                                    />
                                </div>
                                <div className="filter-item input-rwd">
                                    <label className="filter-item-label">{t('label.segment.title')}</label>
                                    <MultiSelectDropdown
                                        options={segmentOptions}
                                        selected={selectedSegments}
                                        toggleOption={handleSegmentChange}
                                        forLabel="filter_segment"
                                        tooltipTxt={segmentTooltipTxt}
                                        visibletooltip={true}
                                    />
                                </div>
                            </div>
                            <div className={'grid-column-second flex' + (!toggleFilters ? ' hidden' : '')}>
                                <div className="filter-item input-rwd">
                                    <label className="filter-item-label">{t('label.fund.fio.title')}</label>
                                    <MultiSelectDropdown
                                        options={umbrellaOptions}
                                        selected={selectedUmbrellas}
                                        toggleOption={handleUmbrellaChange}
                                        forLabel="filter_umbrella"
                                        tooltipTxt={t(umbrellaTooltipTxt)}
                                        visibletooltip={true}
                                    />
                                </div>

                                <div className="filter-item input-rwd">
                                    <label className="filter-item-label">{t('label.product.fio.title')}</label>
                                    <MultiSelectDropdown
                                        options={productOptions}
                                        selected={selectedProducts}
                                        toggleOption={handleProductChange}
                                        forLabel="filter_product"
                                        tooltipTxt={t(productTooltipTxt)}
                                        visibletooltip={true}
                                    />
                                </div>

                                <div className="filter-item input-rwd">
                                    <label className="filter-item-label">{t('label.sri.title')}</label>
                                    <MultiSelectDropdown
                                        options={sriOptions}
                                        selected={selectedSris}
                                        toggleOption={handleSriChange}
                                        forLabel="filter_sri"
                                        tooltipTxt={t('label.sri.tooltip')}
                                        visibletooltip={false}
                                    />
                                </div>

                                <div className="filter-item input-rwd">
                                    <label className="filter-item-label">{t('label.investmentHorizon.title')}</label>
                                    <MultiSelectDropdown
                                        options={investmentHorizonOptions}
                                        selected={selectedInvestmentHorizons}
                                        toggleOption={handleInvestmentHorizonChange}
                                        forLabel="filter_investmentHorizon"
                                        tooltipTxt={t('label.investmentHorizon.tooltip')}
                                        visibletooltip={false}
                                    />
                                </div>
                                <div className="start-search flex">
                                    <label className="filter-item-label">{t('label.search.title')}</label>
                                    <div className="search-bar input-rwd flex">
                                        <input
                                            className="flex"
                                            type="text"
                                            placeholder={t('label.search.placeholder')}
                                            onChange={(event) => setSearchByName(event.target.value)}
                                        />
                                        <span className="search-bar-icon-btn" onClick={handleSearch}></span>
                                    </div>
                                </div>
                                <div className="c-multi-select-dropdown-esg flex">
                                    <div className="c-multi-select-dropdown tooltip flex" style={{height: '40px'}}>
                                        <li className="c-multi-select-dropdown__option flex c-multi-select-dropdown_option-esg">
                                            <Tooltip tooltiptext={t('label.esg.tooltip')} />
                                            <label className="flex">
                                                <input
                                                    id={'filter_isEsg_true'}
                                                    type="checkbox"
                                                    className="c-multi-select-dropdown__option-checkbox"
                                                    readOnly
                                                    checked={ctx.isEsg}
                                                    onChange={(e) => handleIsEsgChange(e.target.checked)}
                                                />
                                                <span className="check-mark"></span>
                                                <span className="check-label flex">
                                                    {t('label.isEsg.title')} <i className="icon-esg leaf" />
                                                </span>
                                            </label>
                                        </li>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="delete more-filters">
                        <div className="delete-all flex">
                            <span onClick={handleToggleFilters} className="flex">
                                {t('label.filters.more', {count: !toggleFilters ? 6 : 0})}
                                {!toggleFilters ? (
                                    <svg
                                        id="check"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="24"
                                        viewBox="0 0 16 9"
                                        fill="#006192">
                                        <path d="M15.707 0.293006C15.316 -0.0979941 14.684 -0.0979941 14.293 0.293006L8.00001 6.58601L1.70701 0.293006C1.31601 -0.0979941 0.684006 -0.0979941 0.293006 0.293006C-0.0979941 0.684006 -0.0979941 1.31601 0.293006 1.70701L7.29301 8.70701C7.48801 8.90201 7.74401 9.00001 8.00001 9.00001C8.25601 9.00001 8.51201 8.90201 8.70701 8.70701L15.707 1.70701C16.098 1.31601 16.098 0.684006 15.707 0.293006Z" />
                                    </svg>
                                ) : (
                                    <svg
                                        id="check"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        transform="rotate(180 0 0)"
                                        height="24"
                                        viewBox="0 0 16 9"
                                        fill="#006192">
                                        <path d="M15.707 0.293006C15.316 -0.0979941 14.684 -0.0979941 14.293 0.293006L8.00001 6.58601L1.70701 0.293006C1.31601 -0.0979941 0.684006 -0.0979941 0.293006 0.293006C-0.0979941 0.684006 -0.0979941 1.31601 0.293006 1.70701L7.29301 8.70701C7.48801 8.90201 7.74401 9.00001 8.00001 9.00001C8.25601 9.00001 8.51201 8.90201 8.70701 8.70701L15.707 1.70701C16.098 1.31601 16.098 0.684006 15.707 0.293006Z" />
                                    </svg>
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            </section>
            <ActiveFilters ctx={ctx} filters={defaultFilters} />
        </>
    )
}

export default FiltersFIO
